import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../../utils/authFetch";

function DocumentManager({ recordId, recordType }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [viewUrl, setViewUrl] = useState("");
  const [loadingView, setLoadingView] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    document.body.classList.contains("dark-mode")
  );

  const navigate = useNavigate();

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.body.classList.contains("dark-mode"));
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const theme = useMemo(() => {
    if (isDarkMode) {
      return {
        eyebrow: "#a5b4fc",
        muted: "#94a3b8",
        text: "#e5e7eb",
        strongText: "#f8fafc",
        cardBg: "rgba(15, 23, 42, 0.92)",
        cardBorder: "rgba(51, 65, 85, 0.9)",
        cardShadow: "0 8px 24px rgba(0, 0, 0, 0.24)",
        emptyBg: "rgba(15, 23, 42, 0.72)",
        emptyBorder: "rgba(71, 85, 105, 0.9)",
        buttonBg: "rgba(15, 23, 42, 0.92)",
        buttonBorder: "rgba(71, 85, 105, 0.95)",
        iconBg: "linear-gradient(135deg, rgba(37, 99, 235, 0.24) 0%, rgba(79, 70, 229, 0.26) 100%)",
        uploadBg: "rgba(15, 23, 42, 0.92)",
        uploadBorder: "rgba(71, 85, 105, 0.95)",
        uploadText: "#e5e7eb",
        uploadNoticeBg: "rgba(37, 99, 235, 0.18)",
        uploadNoticeBorder: "rgba(96, 165, 250, 0.38)",
        uploadNoticeText: "#bfdbfe",
        overlayBg: "rgba(2, 6, 23, 0.72)",
        modalBg: "#0f172a",
        modalHeaderBg: "linear-gradient(180deg, #111827 0%, #0f172a 100%)",
        modalBorder: "rgba(51, 65, 85, 0.95)",
      };
    }

    return {
      eyebrow: "#6366f1",
      muted: "#64748b",
      text: "#334155",
      strongText: "#0f172a",
      cardBg: "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)",
      cardBorder: "rgba(226, 232, 240, 0.95)",
      cardShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
      emptyBg: "rgba(255,255,255,0.78)",
      emptyBorder: "#cbd5e1",
      buttonBg: "#ffffff",
      buttonBorder: "rgba(99, 102, 241, 0.16)",
      iconBg: "linear-gradient(135deg, #e0e7ff 0%, #dbeafe 100%)",
      uploadBg: "rgba(255, 255, 255, 0.9)",
      uploadBorder: "rgba(99, 102, 241, 0.16)",
      uploadText: "#334155",
      uploadNoticeBg: "#eff6ff",
      uploadNoticeBorder: "#bfdbfe",
      uploadNoticeText: "#1d4ed8",
      overlayBg: "rgba(15, 23, 42, 0.55)",
      modalBg: "#ffffff",
      modalHeaderBg: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
      modalBorder: "rgba(226, 232, 240, 0.95)",
    };
  }, [isDarkMode]);

  const loadDocs = async () => {
    try {
      const res = await authFetch(`/api/documents/${recordType}/${recordId}`);
      const data = await res.json();
      setDocs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load documents error:", err);
      setDocs([]);
    }
  };

  useEffect(() => {
    if (recordId) loadDocs();
  }, [recordId]);

  const isPendingSignatureDocument = (doc) => {
    const fileName = String(doc?.file_name || "").toLowerCase();
    const mimeType = String(doc?.mime_type || "").toLowerCase();

    return (
      mimeType === "application/pending" ||
      fileName === "scope of appointment" ||
      fileName.includes("aca consent pending") ||
      fileName.includes("soa pending")
    );
  };

  const isRealFileDocument = (doc) => {
    const fileSize = Number(doc?.file_size || 0);
    const mimeType = String(doc?.mime_type || "").toLowerCase();

    return fileSize > 0 && mimeType !== "application/pending";
  };

  const canViewOrDownload = (doc) => {
    return isRealFileDocument(doc);
  };

  const shouldShowStatusPill = (doc) => {
    return isPendingSignatureDocument(doc);
  };

  const getFileTypeLabel = (doc) => {
    const mimeType = String(doc?.mime_type || "").toLowerCase();

    if (mimeType === "application/pdf") return "PDF";
    if (mimeType.startsWith("image/")) return "Image";
    if (mimeType.includes("word")) return "Word";
    if (mimeType === "text/plain") return "Text";
    if (mimeType === "text/csv") return "CSV";

    return "File";
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await authFetch(`/api/documents/${recordType}/${recordId}`, {
        method: "POST",
        body: formData,
      });

      if (res.ok) {
        await loadDocs();
      } else {
        alert("Upload failed.");
      }
    } catch (err) {
      console.error("Upload document error:", err);
      alert("Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const getDownloadUrl = async (docId) => {
    const res = await authFetch(`/api/documents/download/${docId}`);
    const data = await res.json();

    if (!res.ok || !data?.download_url) {
      throw new Error(data?.error || "Download failed.");
    }

    return data.download_url;
  };

  const downloadFile = async (docId) => {
    try {
      const downloadUrl = await getDownloadUrl(docId);
      window.open(downloadUrl, "_blank");
    } catch (err) {
      console.error("Download document error:", err);
      alert(err.message || "Download failed.");
    }
  };

  const openViewer = async (doc) => {
    try {
      setLoadingView(true);
      setViewDoc(doc);
      setViewUrl("");

      const downloadUrl = await getDownloadUrl(doc.id);
      setViewUrl(downloadUrl);
    } catch (err) {
      console.error("Open document viewer error:", err);
      setViewDoc(null);
      setViewUrl("");
      alert(err.message || "Unable to open document.");
    } finally {
      setLoadingView(false);
    }
  };

  const closeViewer = () => {
    setViewDoc(null);
    setViewUrl("");
    setLoadingView(false);
  };

  const statusConfig = (status) => {
    if (status === "sent") {
      return isDarkMode
        ? {
            label: "Sent",
            bg: "rgba(245, 158, 11, 0.2)",
            color: "#fde68a",
            border: "rgba(251, 191, 36, 0.34)",
          }
        : {
            label: "Sent",
            bg: "#fef3c7",
            color: "#b45309",
            border: "#fde68a",
          };
    }

    if (status === "signed") {
      return isDarkMode
        ? {
            label: "Ready to Complete",
            bg: "rgba(37, 99, 235, 0.22)",
            color: "#bfdbfe",
            border: "rgba(96, 165, 250, 0.34)",
          }
        : {
            label: "Ready to Complete",
            bg: "#dbeafe",
            color: "#1d4ed8",
            border: "#bfdbfe",
          };
    }

    if (status === "completed") {
      return isDarkMode
        ? {
            label: "Completed",
            bg: "rgba(22, 163, 74, 0.2)",
            color: "#bbf7d0",
            border: "rgba(74, 222, 128, 0.34)",
          }
        : {
            label: "Completed",
            bg: "#dcfce7",
            color: "#15803d",
            border: "#bbf7d0",
          };
    }

    return isDarkMode
      ? {
          label: status || "Pending",
          bg: "rgba(15, 23, 42, 0.86)",
          color: "#cbd5e1",
          border: "rgba(148, 163, 184, 0.36)",
        }
      : {
          label: status || "Pending",
          bg: "#f1f5f9",
          color: "#475569",
          border: "#e2e8f0",
        };
  };

  const fileTypeConfig = (doc) => {
    return isDarkMode
      ? {
          label: getFileTypeLabel(doc),
          bg: "rgba(37, 99, 235, 0.18)",
          color: "#bfdbfe",
          border: "rgba(96, 165, 250, 0.34)",
        }
      : {
          label: getFileTypeLabel(doc),
          bg: "#eff6ff",
          color: "#1d4ed8",
          border: "#bfdbfe",
        };
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
          marginBottom: "14px",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.03em",
              textTransform: "uppercase",
              color: theme.eyebrow,
              marginBottom: "4px",
            }}
          >
            File Workspace
          </div>

          <div style={{ fontSize: "14px", color: theme.muted }}>
            Upload, review, and manage documents for this record.
          </div>
        </div>

        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            border: `1px solid ${theme.uploadBorder}`,
            background: theme.uploadBg,
            color: theme.uploadText,
            borderRadius: "12px",
            padding: "10px 14px",
            fontSize: "12px",
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontSize: "14px" }}>＋</span>
          Upload Document
          <input
            type="file"
            onChange={uploadFile}
            style={{ display: "none" }}
          />
        </label>
      </div>

      {uploading && (
        <div
          style={{
            marginBottom: "14px",
            padding: "12px 14px",
            borderRadius: "12px",
            background: theme.uploadNoticeBg,
            border: `1px solid ${theme.uploadNoticeBorder}`,
            color: theme.uploadNoticeText,
            fontSize: "13px",
            fontWeight: 600,
          }}
        >
          Uploading document...
        </div>
      )}

      {docs.length === 0 ? (
        <div
          style={{
            border: `1px dashed ${theme.emptyBorder}`,
            borderRadius: "16px",
            padding: "24px",
            background: theme.emptyBg,
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: 700,
              color: theme.strongText,
              marginBottom: "6px",
            }}
          >
            No documents yet
          </div>

          <div style={{ fontSize: "13px", color: theme.muted }}>
            Upload a file to start building the document history for this record.
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {docs.map((doc) => {
            const status = statusConfig(doc.status);
            const fileType = fileTypeConfig(doc);
            const downloadable = canViewOrDownload(doc);
            const signatureStatus = shouldShowStatusPill(doc);

            return (
              <div
                key={doc.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "14px",
                  padding: "14px 16px",
                  borderRadius: "16px",
                  border: `1px solid ${theme.cardBorder}`,
                  background: theme.cardBg,
                  boxShadow: theme.cardShadow,
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "12px",
                      background: theme.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "18px",
                      flexShrink: 0,
                    }}
                  >
                    📄
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: 700,
                        color: theme.strongText,
                        wordBreak: "break-word",
                      }}
                    >
                      {doc.file_name}
                    </div>

                    <div
                      style={{
                        marginTop: "6px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      {signatureStatus ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            borderRadius: "999px",
                            padding: "4px 9px",
                            fontSize: "11px",
                            fontWeight: 700,
                            background: status.bg,
                            color: status.color,
                            border: `1px solid ${status.border}`,
                          }}
                        >
                          {status.label}
                        </span>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            borderRadius: "999px",
                            padding: "4px 9px",
                            fontSize: "11px",
                            fontWeight: 700,
                            background: fileType.bg,
                            color: fileType.color,
                            border: `1px solid ${fileType.border}`,
                          }}
                        >
                          {fileType.label}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {downloadable && (
                    <button
                      type="button"
                      onClick={() => openViewer(doc)}
                      style={{
                        border: `1px solid ${theme.buttonBorder}`,
                        background: theme.buttonBg,
                        color: theme.text,
                        borderRadius: "10px",
                        padding: "8px 12px",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      View
                    </button>
                  )}

                  {downloadable && (
                    <button
                      type="button"
                      onClick={() => downloadFile(doc.id)}
                      style={{
                        border: `1px solid ${theme.buttonBorder}`,
                        background: theme.buttonBg,
                        color: theme.text,
                        borderRadius: "10px",
                        padding: "8px 12px",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Download
                    </button>
                  )}

                  {doc.status === "signed" && isPendingSignatureDocument(doc) && (
                    <button
                      type="button"
                      onClick={() => navigate(`/soa/complete/${doc.file_key}`)}
                      style={{
                        border: "none",
                        background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                        color: "#ffffff",
                        borderRadius: "10px",
                        padding: "9px 13px",
                        fontSize: "12px",
                        fontWeight: 700,
                        cursor: "pointer",
                        boxShadow: "0 10px 22px rgba(79, 70, 229, 0.20)",
                      }}
                    >
                      Complete SOA
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewDoc && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: theme.overlayBg,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            padding: "24px",
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              width: "min(1100px, 96vw)",
              height: "min(820px, 92vh)",
              background: theme.modalBg,
              borderRadius: "22px",
              boxShadow: isDarkMode
                ? "0 30px 80px rgba(0, 0, 0, 0.48)"
                : "0 30px 80px rgba(15, 23, 42, 0.28)",
              border: `1px solid ${theme.modalBorder}`,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                padding: "14px 18px",
                borderBottom: `1px solid ${theme.modalBorder}`,
                background: theme.modalHeaderBg,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: theme.strongText,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {viewDoc.file_name}
                </div>

                <div style={{ fontSize: "12px", color: theme.muted, marginTop: "2px" }}>
                  Document Preview
                </div>
              </div>

              <button
                type="button"
                onClick={closeViewer}
                style={{
                  border: `1px solid ${theme.buttonBorder}`,
                  background: theme.buttonBg,
                  color: theme.text,
                  borderRadius: "10px",
                  padding: "8px 12px",
                  fontSize: "12px",
                  fontWeight: 700,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Close
              </button>
            </div>

            {loadingView || !viewUrl ? (
              <div
                style={{
                  height: "calc(100% - 62px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: theme.muted,
                  fontSize: "14px",
                  fontWeight: 700,
                }}
              >
                Loading document...
              </div>
            ) : (
              <iframe
                title="Document Viewer"
                src={viewUrl}
                width="100%"
                height="100%"
                style={{ border: "none", height: "calc(100% - 62px)" }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentManager;